import { filter } from 'lodash';
import { Icon } from '@iconify/react';
import { sentenceCase } from 'change-case';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
// material
import { useTheme } from '@material-ui/core/styles';
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Box,
  CircularProgress,
  Tooltip
} from '@material-ui/core';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getUserList, deleteUser } from '../../redux/slices/user';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// components
import Page from '../../components/Page';
import LoadingScreen from 'src/components/LoadingScreen';
import Label from '../../components/Label';
import SearchNotFound from '../../components/SearchNotFound';
import useTableMaxHeight from 'src/hooks/useTableMaxHeight';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../../components/_dashboard/user/list';
import  CustomerOrderToolbar  from 'src/components/_dashboard/user/list/CustomerOrderToolbar';
import  ProductsMenu  from 'src/components/_dashboard/user/list/ProductsMenu.js';
import axios from 'axios';
import {COMPANY_NAME, REST_API, REST_API_END_POINT} from '../../constants/Defaultvalues';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import ConfirmationPopup from 'src/components/_dashboard/official-request/ConfirmationPopup';
import { KeyboardBackspaceOutlined } from '@material-ui/icons';
import Modal from 'src/components/_dashboard/blog/ProjectModel';
import AddCommunityType from 'src/components/_dashboard/blog/AddCommunityType';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'addedby', label: 'Added By', alignRight: false },
  { id: 'addedon', label: 'Added On', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'options', label: '', alignRight: false},      
];

// id,name,description	,	type,phone,email,address, thumbnail, status, authorized_by, timestamp
// ----------------------------------------------------------------------

// Alphanumeric comparison helper function (natural sort)
function alphanumericCompare(str1, str2) {
  const normalize = (str) => {
    if (str === null || str === undefined) return '';
    // Strip leading and trailing quotes before comparison
    let normalized = str.toString().trim();
    // Remove leading and trailing quotes (both single and double)
    normalized = normalized.replace(/^["']+|["']+$/g, '');
    return normalized;
  };

  const a = normalize(str1);
  const b = normalize(str2);

  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;

  const getChunks = (str) => {
    const chunks = [];
    let currentChunk = '';
    let chunkType = null; // 'hash', 'otherSpecial', 'digit', or 'letter'

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const isDigit = /\d/.test(char);
      const isLetter = /[a-zA-Z]/.test(char);
      const isHash = char === '#';
      const isOtherSpecial = !isDigit && !isLetter && !isHash;
      
      // Determine chunk type with priority: hash < otherSpecial < digit < letter
      let currentType;
      if (isHash) {
        currentType = 'hash';
      } else if (isOtherSpecial) {
        currentType = 'otherSpecial';
      } else if (isDigit) {
        currentType = 'digit';
      } else {
        currentType = 'letter';
      }

      if (i === 0) {
        chunkType = currentType;
        currentChunk = char;
      } else if (currentType === chunkType) {
        currentChunk += char;
      } else {
        chunks.push({
          value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
          type: chunkType
        });
        currentChunk = char;
        chunkType = currentType;
      }
    }

    if (currentChunk) {
      chunks.push({
        value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
        type: chunkType
      });
    }

    return chunks;
  };

  const chunksA = getChunks(a);
  const chunksB = getChunks(b);
  const len = Math.min(chunksA.length, chunksB.length);

  // Type priority: hash (0) < otherSpecial (1) < digit (2) < letter (3)
  const getTypePriority = (type) => {
    if (type === 'hash') return 0;
    if (type === 'otherSpecial') return 1;
    if (type === 'digit') return 2;
    return 3; // letter
  };

  for (let i = 0; i < len; i++) {
    const chunkA = chunksA[i];
    const chunkB = chunksB[i];

    const priorityA = getTypePriority(chunkA.type);
    const priorityB = getTypePriority(chunkB.type);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same type, compare values
    if (chunkA.type === 'digit') {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    } else {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    }
  }

  if (chunksA.length < chunksB.length) return -1;
  if (chunksA.length > chunksB.length) return 1;

  return 0;
}

function descendingComparator(a, b, orderBy) {
  // Use alphanumeric comparison for 'name' field
  if (orderBy === 'name') {
    return alphanumericCompare(b[orderBy], a[orderBy]);
  }
  // Default comparison for other fields
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  let filteredArray = array;
  
  // Apply search filter first
  if (query) {
    filteredArray = filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  
  // Sort the filtered array
  const stabilizedThis = filteredArray.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  
  return stabilizedThis.map((el) => el[0]);
}

function scrollCommunityTypePaginationToTop(anchorEl, tableScrollBoxEl, tableContainerEl) {
  if (typeof window === 'undefined') return
  const { documentElement, body } = document
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  documentElement.scrollTop = 0
  body.scrollTop = 0

  let el = anchorEl
  while (el) {
    const { overflowY } = window.getComputedStyle(el)
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      el.scrollHeight > el.clientHeight
    ) {
      el.scrollTop = 0
    }
    el = el.parentElement
  }

  const scrollBox = tableScrollBoxEl
  if (scrollBox) {
    scrollBox.scrollTop = 0
    if (typeof scrollBox.scrollTo === 'function') {
      scrollBox.scrollTo({
        top: 0,
        left: scrollBox.scrollLeft,
        behavior: 'auto',
      })
    }
  }
  const tableWrap = tableContainerEl
  if (tableWrap) {
    const left = tableWrap.scrollLeft
    tableWrap.scrollTop = 0
    if (typeof tableWrap.scrollTo === 'function') {
      tableWrap.scrollTo({ top: 0, left, behavior: 'auto' })
    }
  }
}

export default function CommunityType(props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableContainerRef = useRef(null)
  const tableScrollBoxRef = useRef(null)
  const paginationScrollAnchorRef = useRef(null)
  const skipPaginationScrollRef = useRef(true)
  const [communityType, setCommunityType] = useState([]);
  const [openDeletePop,setOpenDeletePop] =useState(false);
  const [deletingId,setDeletingId] = useState();
  const [deletingName,setDeletingName] = useState('');
  const [openModal,setOpenModal] = useState(false);
  const [editTypeId,setEditTypeId] = useState();

  const user = JSON.parse(localStorage.getItem('user'))
  const userId = user.user_id
  const explodedPermissions = user?.exploded_permissions
  const location = useLocation();
  const receivedData = location.state?.data;

  useEffect(() => {
    dispatch(getUserList());
  }, [dispatch]);

  const fetchData = async () => {
    setLoading(true)
    try{
      const response = await axios.post(`${REST_API_END_POINT}community-type/get-all`)
      const communityTypeData = response?.data?.results;
      if(communityTypeData){
        // Sort community types alphanumerically by name
        const sortedCommunityTypes = [...communityTypeData].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
        setCommunityType(sortedCommunityTypes);
        // console.log("communityType : ",communityType);
      }
      else{
        setCommunityType([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    finally{
      setLoading(false);
    }
  }
  useEffect( async () => {
    try {
      fetchData();
    }
      catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [receivedData, openModal]);

  // ...
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const changeStatus = async (comId, comStatus) => {
    try {
      let deleteId = comId;
      let status = comStatus;
      let response = await axios.post(`${REST_API_END_POINT}community-type/delete`,{deleteId,status,userId});
    //   console.log("response-----------------", response.data);
      if (response.data.status === 1) {
        enqueueSnackbar(response.data.message, { variant: 'success' });
        navigate(PATH_DASHBOARD.community.type, { state: { data: `${response.data.message}+${deleteId}` } });
      }
      else if (response.data.status === 0) {
        enqueueSnackbar(response.data.message, { variant: 'error' });
        navigate(PATH_DASHBOARD.community.type, { state: { data: `${response.data.message}+${deleteId}` } });
      }
      else {
        console.log("Error.........!");
        enqueueSnackbar("Server Error...!", { variant: 'error' });
        navigate(PATH_DASHBOARD.community.type, { state: { data: `${response.data.message}+${deleteId}` } });
      }
    }
    catch (error) {
      console.log("Delete error : ", error);
    }
  }

  const handleMenuClick = async (id, status) => {
    let newStatus;
    (status === 1) ? newStatus = 0 : newStatus = 1
    // console.log("id, newStatus",id, newStatus);
    await changeStatus(id, newStatus);
  }

  const handleDeleteClick = async (id, newStatus) => {
    // console.log("id, newStatus",id, newStatus);
    setDeletingName('');
    setDeletingId();
    setOpenDeletePop(false);
    await changeStatus(id, newStatus);
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useLayoutEffect(() => {
    if (skipPaginationScrollRef.current) {
      skipPaginationScrollRef.current = false
      return
    }
    const run = () =>
      scrollCommunityTypePaginationToTop(
        paginationScrollAnchorRef.current,
        tableScrollBoxRef.current,
        tableContainerRef.current
      )
    run()
    requestAnimationFrame(() => {
      requestAnimationFrame(run)
    })
  }, [page, rowsPerPage])

  const tableMaxHeight = useTableMaxHeight(
    tableScrollBoxRef,
    [loading, communityType?.length, rowsPerPage, filterName]
  );

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0)
  };

  const handleDeleteUser = (userId) => {
    dispatch(deleteUser(userId));
  };

  const handleback = () => {
    // window.history.back()
    navigate(PATH_DASHBOARD.community.root)
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - communityType?.length) : 0;
  const filteredUsers = applySortFilter(communityType, getComparator(order, orderBy), filterName);
  const slicedData = filteredUsers?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isUserNotFound = filteredUsers?.length === 0;

  return (
    <Page title={`Project Type: List | ${COMPANY_NAME}`}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button sx={{ mb :2 }}
                variant="outlined"
                type="submit"
                onClick={() => handleback()}
                startIcon={<KeyboardBackspaceOutlined />}
            >
                Back
            </Button>
            <HeaderBreadcrumbs sx={{ flexGrow: 1, margin: '20px 20px' }} heading="Project Type" />
            <Button sx={{ mb :2 }}
              disabled={explodedPermissions?.some(permission => ['Manage Project Types'].includes(permission)) ? false : true}
              onClick={() => {setOpenModal(true)
              // navigate(PATH_DASHBOARD.community.addtype);
              }}
              variant="contained"
              startIcon={<Icon icon={plusFill} />}
            >
                Add Project Type
            </Button>
        </Stack>

        <Card>
          <CustomerOrderToolbar 
            filterName={filterName} 
            onFilterName={handleFilterByName} 
            item="project types"
          />

          <Box ref={paginationScrollAnchorRef}>
          <Box
            ref={tableScrollBoxRef}
            sx={{
              maxHeight: tableMaxHeight,
              overflowY: 'auto',
              overflowX: 'hidden',
              width: '100%',
              '&::-webkit-scrollbar': {
                width: '0.6em',
                height: '0.6em',
              },
              '&::-webkit-scrollbar-track': {
                background: '#1e1e1e',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#555',
                borderRadius: '6px',
                border: '2px solid #1e1e1e',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#888',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#1e1e1e',
              },
            }}
          >
            <TableContainer
              ref={tableContainerRef}
              sx={{
                minWidth: 800,
                whiteSpace: 'nowrap',
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  width: '0.6em',
                  height: '0.6em',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#1e1e1e', // dark background to blend with dark UI
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#555', // medium-gray thumb
                  borderRadius: '6px',
                  border: '2px solid #1e1e1e', // contrast border to separate from track
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#888', // lighter on hover for feedback
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: '#1e1e1e' // match your dark background
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  '& .MuiTableCell-head': {
                    padding: '8px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    height: '48px',
                    minHeight: '48px',
                    maxHeight: '48px',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    verticalAlign: 'middle'
                  },
                  '& .MuiTableSortLabel-root': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    justifyContent: 'flex-start',
                    '& .MuiTableSortLabel-icon': {
                      marginLeft: '4px'
                    }
                  }
                }}
              >
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={slicedData?.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {loading ? (
                    <Stack alignItems="center" justifyContent="center" style={{ height: '100%',width:'100%' }}>
                    <CircularProgress
                    color="primary"
                    variant="indeterminate"
                    />
                  </Stack>
                  ) : (
                    <>
                    {slicedData?.map((row) => {
                      let { id,name,status, authorized_by, timestamp } = row;
                      let statusText;
                      (status === 1) ? statusText = "Active" : statusText = "Inactive";
                      let addedon = moment.unix(timestamp).format('DD-MM-YYYY');
                      return (
                        <TableRow
                          hover
                          key={id}
                          tabIndex={-1}
                          role="checkbox"
                          sx={{
                            '& .MuiTableCell-root': {
                              padding: '8px 12px',
                              fontSize: '0.75rem',
                              height: 'auto',
                              lineHeight: 1.4
                            }
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ padding: '8px 4px !important' }}>
                          </TableCell>
                          <TableCell align="left" sx={{ padding: '8px 12px' }}>
                            <Tooltip title={name}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.75rem',
                                  fontWeight: 500
                                }}
                              >
                                {name}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="left" sx={{ padding: '8px 12px' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {authorized_by}
                            </Typography>
                          </TableCell>
                          <TableCell align="left" sx={{ padding: '8px 12px' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {addedon}
                            </Typography>
                          </TableCell>
                          <TableCell align="left" sx={{ padding: '8px 12px' }}>
                            <Label
                              variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                              color={(status === 1 && 'info') || 'error'}
                            >
                              {sentenceCase(statusText)}
                            </Label>
                          </TableCell>

                          <TableCell align="left" sx={{ padding: '8px 12px' }}>
                            {/* <ProductsMenu onDelete={() => handleDeleteUser(id)} userName={name} id={id} status={status}/> */}
                            <UserMoreMenu
                                onDelete={()=>{if(explodedPermissions?.some(permission => ['Manage Project Types'].includes(permission))){
                                  setOpenDeletePop(true);setDeletingId(id);setDeletingName(name);
                                }else{
                                  enqueueSnackbar("Access Denied: You do not have sufficient permissions", { variant: 'error' })
                                }}}
                                onEdit={()=>{if(explodedPermissions?.some(permission => ['Manage Project Types'].includes(permission))){
                                  setOpenModal(true); setEditTypeId(id)
                                }else{
                                  enqueueSnackbar("Access Denied: You do not have sufficient permissions", { variant: 'error' })
                                }}}
                                onStatusChange={()=>{if(explodedPermissions?.some(permission => ['Manage Project Types'].includes(permission))){
                                  handleMenuClick(id,status)
                                }else{
                                  enqueueSnackbar("Access Denied: You do not have sufficient permissions", { variant: 'error' })
                                }}}
                                status={status}
                                oppName={name}
                                oppId={id}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                    </>
                  )}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} msg="Project type"/>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Box>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50,100]}
            component="div"
            count={filteredUsers?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          </Box>
        </Card>
      </Container>

      {/* Confirmation for deleting community */}
      <ConfirmationPopup
        open={openDeletePop}
        handleClose={()=>{setOpenDeletePop(false)}}
        message={`You want to delete ${deletingName} ! `}
        handleClick={()=>{handleDeleteClick(deletingId, 2)}}
      />

    <Modal
      modalTitle={editTypeId ? 'Edit Project Type' : 'Add Project Type'}
      open={openModal}
      handleClose={() => {setOpenModal(false); setEditTypeId()}}
    >
        <AddCommunityType setOpenModal={()=>setOpenModal(!openModal)} editTypeId={editTypeId} setEditTypeId={()=>setEditTypeId()}/>
    </Modal>
    </Page>
  );
}
